import type { TextComposerPreviewContent } from "./useMessagePreview";
import type {
  TextComposerFallbackChannel,
  TextComposerPrimaryChannel,
} from "../channel/channelDerivation";
import {
  isNormalizedAuthoringStateEmpty,
  type TextComposerAuthoringState,
} from "../editor/authoringState";
import { parseGuardedProductFieldDtl } from "../extensions/mergeTags/productFieldDtl";
import type { TextComposerSuggestedActionType } from "../extensions/suggestedActions/suggestedActionsSchema";

export type PhoneChannel = TextComposerPrimaryChannel;
export type PhoneScreen = "app" | "lock";

export const EMPTY_COMPOSER_HINT = "Start typing to see your message...";
export const PLACEHOLDER_SENDING_NUMBER = "+1 (555) 555-0123";

export type PhoneSuggestionType = TextComposerSuggestedActionType | "PRODUCT";

const PILL_TYPES = new Set<PhoneSuggestionType>([
  "REPLY",
  "OPEN_URL",
  "DIAL_PHONE",
  "PRODUCT",
]);

export interface PhoneMessageSuggestion {
  type: PhoneSuggestionType;
  text: string;
}

export interface PhoneMessage {
  channel: PhoneChannel;
  channelLabel: "Text Message" | "MMS Message" | "RCS Message";
  sender: string;
  text: string;
  showEmptyHint: boolean;
  imageUrl: string | null;
  imageAlt: string;
  documentName: string | null;
  ctaText: string | null;
  suggestions: PhoneMessageSuggestion[];
  isError: boolean;
  keepBubbleMinHeight: boolean;
}

export interface PhoneMessageInput {
  activeChannel: TextComposerPrimaryChannel | TextComposerFallbackChannel;
  previewByChannel: {
    RCS: TextComposerPreviewContent | null;
    SMS: TextComposerPreviewContent | null;
    MMS: TextComposerPreviewContent | null;
  };
  mediaNameByUrl: Record<string, string>;
  brandName?: string | null;
  authoringState?: TextComposerAuthoringState;
  isPreviewError?: boolean;
  readOnlyPreviewIsEmpty?: boolean;
}

const CHANNEL_LABEL = {
  RCS: "RCS Message",
  MMS: "MMS Message",
  SMS: "Text Message",
  INVALID: "Text Message",
} as const satisfies Record<PhoneChannel, PhoneMessage["channelLabel"]>;

function derivePhoneSuggestions(
  resolved: TextComposerPreviewContent["suggestions"],
  authored: TextComposerAuthoringState["suggestions"],
): PhoneMessageSuggestion[] {
  if (resolved) {
    return resolved
      .map((suggestion) => ({
        type:
          suggestion.type &&
          PILL_TYPES.has(suggestion.type as PhoneSuggestionType)
            ? (suggestion.type as PhoneSuggestionType)
            : ("OPEN_URL" as const),
        text: suggestion.text?.trim() ?? "",
      }))
      .filter((suggestion) => suggestion.text.length > 0);
  }
  return (authored ?? [])
    .map((suggestion) => {
      const nameRef = parseGuardedProductFieldDtl(suggestion.text);
      const isProductLink =
        "url" in suggestion &&
        parseGuardedProductFieldDtl(suggestion.url)?.field === "item_url";
      return {
        type: isProductLink ? ("PRODUCT" as const) : suggestion.type,
        text:
          nameRef?.field === "name"
            ? `Product ${nameRef.index + 1} name`
            : suggestion.text.trim(),
      };
    })
    .filter((suggestion) => suggestion.text.length > 0);
}

export function derivePhoneMessage({
  activeChannel,
  previewByChannel,
  mediaNameByUrl,
  brandName,
  authoringState,
  isPreviewError = false,
  readOnlyPreviewIsEmpty = false,
}: PhoneMessageInput): PhoneMessage {
  const channel: PhoneChannel = activeChannel ?? "INVALID";
  const body =
    channel === "INVALID" ? undefined : previewByChannel[channel]?.body;
  const isRcs = channel === "RCS";
  const hasMediaChannel = isRcs || channel === "MMS";
  const url = typeof body?.url === "string" ? body.url : null;

  const imageUrl =
    !readOnlyPreviewIsEmpty && hasMediaChannel && body?.type === "IMAGE"
      ? url
      : null;
  const documentUrl =
    !readOnlyPreviewIsEmpty && hasMediaChannel && body?.type === "DOCUMENT"
      ? url
      : null;
  const bodyText = body?.text ?? "";

  const showEmptyHint = readOnlyPreviewIsEmpty
    ? false
    : imageUrl !== null || documentUrl !== null
      ? false
      : authoringState !== undefined
        ? isNormalizedAuthoringStateEmpty(authoringState)
        : !bodyText;

  const buttonText =
    !readOnlyPreviewIsEmpty && isRcs
      ? (previewByChannel.RCS?.buttons?.[0]?.text ?? "")
      : "";

  const suggestions =
    !readOnlyPreviewIsEmpty && isRcs
      ? derivePhoneSuggestions(
          previewByChannel.RCS?.suggestions,
          authoringState?.suggestions,
        )
      : [];

  return {
    channel,
    channelLabel: CHANNEL_LABEL[channel],
    sender: isRcs
      ? brandName?.trim() || "Your Brand"
      : PLACEHOLDER_SENDING_NUMBER,
    text: showEmptyHint ? bodyText || EMPTY_COMPOSER_HINT : bodyText,
    showEmptyHint,
    imageUrl,
    imageAlt:
      (imageUrl && mediaNameByUrl[imageUrl]) ??
      (isRcs ? "RCS media" : "MMS media"),
    documentName: documentUrl
      ? (mediaNameByUrl[documentUrl] ?? "Contact card")
      : null,
    ctaText: buttonText || null,
    suggestions,
    isError: isPreviewError,
    keepBubbleMinHeight: readOnlyPreviewIsEmpty,
  };
}
